'use client';

import { Box } from '@mui/material';
import { createContext, useMemo, type PropsWithChildren } from 'react';

import { Loader } from '@/components/layout/loading';
import type { nonProjectDalFactory } from '@/data/dal/dalFactory';
import { projectDalFactory } from '@/data/dal/dalFactory';

import { useEnvironment } from './useEnvironment';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';

export const DalContext = createContext<
  | (ReturnType<typeof projectDalFactory> &
      ReturnType<typeof nonProjectDalFactory>)
  | null
>(null);

export const DalProvider = ({ children }: PropsWithChildren) => {
  const { currentProjectId } = useUser();

  if (!currentProjectId) {
    return <LoaderComponent />;
  }

  return <ProviderWrapper>{children}</ProviderWrapper>;
};

const ProviderWrapper = ({ children }: PropsWithChildren) => {
  const { nonProjectDal, httpClient, configuration } = useNonProjectDal();
  const { currentProjectId } = useUser();
  const { exposedTestingGlobals, isDev } = useEnvironment();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dal = useMemo(() => {
    const projectDal = projectDalFactory(
      httpClient,
      configuration,
      currentProjectId
    );

    const dal = { ...projectDal, ...nonProjectDal };

    if (exposedTestingGlobals && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).populateTestData = async () => {
        await httpClient.put(
          `/projects/${currentProjectId}/populate?version=1`,
          '{}'
        );
        await httpClient.put(
          `/projects/${currentProjectId}/metering/populate?version=1`,
          '{}'
        );
        await httpClient.put(
          `/projects/${currentProjectId}/inventory/populate-backup-jobs?version=1`,
          '{}'
        );
        await httpClient.put(
          `/projects/${currentProjectId}/visibility/populate?version=1`,
          '{}'
        );
        await httpClient.put(
          `/projects/${currentProjectId}/inventory/populate?version=1`,
          '{}'
        );
        await httpClient.put(
          `/projects/${currentProjectId}/search/populate?version=1`,
          '{}'
        );
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).cleanPopulatedTestData = async () => {
        await httpClient.delete(
          `/projects/${currentProjectId}/search/populate?version=1`
        );
      };
    }

    if (isDev && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dal = dal;
    }

    return dal;
  }, [
    configuration,
    currentProjectId,
    exposedTestingGlobals,
    httpClient,
    isDev,
    nonProjectDal,
  ]);

  return <DalContext.Provider value={dal}>{children}</DalContext.Provider>;
};

const LoaderComponent = () => (
  <Box
    className='h-full'
    sx={() => ({
      background: 'var(--mui-palette-background-pageGradient)',
    })}
  >
    <Loader />
  </Box>
);
